<script setup lang="ts">
import {useImageCdn} from '~/composables/useImageCdn';
const { $loader } = useNuxtApp();
const {getImageUrl, fixedWidthImageParams} = useImageCdn();
const loadingStore = useWadLoadingStore()
const { loading } = storeToRefs(loadingStore)

// On companies.* the root 302s to /dashboard, whose guard bounces an anonymous
// visitor back to /login - three hops to stand still. Keep the logo on the page
// there; www keeps the public homepage.
const logoHref = useIsCompaniesHost() ? '/login' : '/'

if (process.client) {
  watch(loading, (val) => {
    if (val) {
      $loader.start();
    } else {
      $loader.finish();
    }
  })
}
</script>
<template>
  <div class="wad4-auth" :class="{'wad4-auth--loading': loading}">
    <section class="wad4-auth__left">
      <div class="wad4-auth__container">
        <a :href="logoHref" class="wad4-auth__logo">
          <svg xmlns="http://www.w3.org/2000/svg" width="86" height="27" viewBox="0 0 86 27" fill="none">
            <g clip-path="url(#clip0_4308_42482)">
              <path d="M46.7631 0.355469H40.513L33.6111 18.3497L26.7092 0.355469H20.4592L13.5572 18.3497L6.64986 0.355469H0.405273L10.435 26.4896H16.6795L23.5815 8.49534L30.4888 26.4896H36.7334L43.6408 8.49534L50.5427 26.4896H56.7873L46.7631 0.355469Z" fill="#FF174D"/>
              <path d="M59.915 0.355469V6.56171L77.7943 13.4198L59.915 20.2779V26.4896L85.8794 16.5257V10.3194V10.3139L59.915 0.355469Z" fill="#FF174D"/>
            </g>
            <defs>
              <clipPath id="clip0_4308_42482">
                <rect width="86" height="26.8408" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        </a>
        <slot/>
      </div>
      <Footer footer-type="auth"/>
    </section>

    <section class="wad4-auth__right">
      <img class="wad4-auth__background"
           v-bind="fixedWidthImageParams(getImageUrl('/public/signup/signup-artwork.png'), 900, null, '')"
           sizes="(max-width: 768px) 100vw, 50vw"
           alt="Auth side image"
      />
    </section>
  </div>
</template>

<style lang="scss">
.wad4-auth {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 100vh;
  height: auto;
  gap: 0;
  flex: 1 0 0;
  align-self: stretch;
  background: $wad4-c-background-bg-primary;

  &--loading {
    opacity: 0.5;
    pointer-events: none !important;
  }

  &__divider {
    color: $wad4-c-text-text-tertiery;
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    width: 100%;
    position: relative;
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: calc(50% - 1px);
      width: calc(50% - 20px);
      background: $wad4-c-border-border-primary;
      height: 1px;
    }
    &:after {
      content: '';
      position: absolute;
      right: 0;
      top: calc(50% - 1px);
      width: calc(50% - 20px);
      background: $wad4-c-border-border-primary;
      height: 1px;
    }
  }

  &__left {
    display: flex;
    padding: $wad4-viewport-section-padding $wad4-container-padding;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    width: 50%;
    max-width: 900px;
    flex-shrink: 0;
    gap: $wad4-spacing-5xl;
    @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
      width: 100%;
      padding: $wad4-spacing-4xl $wad4-container-padding;
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $wad4-spacing-5xl;
    color: $wad4-c-text-text-primary;
    @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
      align-self: stretch;
    }

    &-wrapper {
      display: flex;
      width: 368px;
      flex-direction: column;
      align-items: flex-start;
      gap: $wad4-spacing-3xl;
      color: $wad4-c-text-text-primary;

      @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
        max-width: 368px;
        width: 100%;
      }
    }

    &-data {
      display: flex;
      width: 368px;
      flex-direction: column;
      align-items: flex-start;
      gap: $wad4-spacing-6xl;
    }

    &__button {
      margin-top: $wad4-spacing-2xl;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
    align-self: stretch;

    &__inputs {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: $wad4-spacing-md;
      align-self: stretch;
    }
  }

  &__logo {
    display: flex;
    width: 86px;
    height: 26.841px;
    padding: 0.356px 0.121px 0.351px 0.405px;
    justify-content: center;
    align-items: center;
    align-self: flex-start;
  }

  &__right {
    align-self: stretch;
    background-color: $wad4-c-background-bg-brand-tertiary;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
    overflow: hidden;
    min-width: 50%;
    flex-grow: 1;
    @include mqBreakPoint(0, $wad4-breakpoint-tablet-max) {
      display: none;
    }
  }

  &__background {
    width: 100%;
    max-width: 900px;
  }

  &__title {
    color: $wad4-c-text-text-primary;
    font-family: $wad4-default-font-sans;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
    margin: 0;
  }

  &__text {
    margin: 0;
  }

  &__links {
    color: $wad4-c-text-text-primary;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    gap: $wad4-spacing-lg;
  }

  &__link {
    color: $wad4-c-text-text-primary;
    align-self: center;
    font-size: 14px;

    a {
      text-decoration: underline !important;
      color: $wad4-c-text-text-primary !important;
    }
  }
}
</style>
